// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Alert, Card, CardContent, Chip, Typography } from '@mui/material';
import { useProfilingData } from '@shared/advisory/client-profile/providers';
import { useClientData } from '@shared/advisory/client-profile/providers';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { type FC } from 'react';

export const ReviewStep: FC = () => {
  const { client } = useClientData();

  const { data } = useProfilingData();

  return (
    <div className="flex flex-col gap-4" data-testid="review-step">
      <div className="flex flex-col items-center gap-2">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-positive/20">
          <AppIcon icon="CheckCircle" className="size-6" color="success" />
        </div>
        <Typography variant="h5" className="!text-xl" data-testid="title">
          <Translation i18nKey="activation-step5.title" ns="components" />
        </Typography>
        <p data-testid="description">
          <Translation
            i18nKey="activation-step5.description"
            ns="components"
            values={{
              clientName: client?.name,
            }}
          />
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Card className="col-span-3" variant="outlined" data-testid="account-card">
          <CardContent className="!p-4">
            <Typography variant="h5" className="!flex !items-center !gap-1">
              <AppIcon icon="Building" className="size-5" color="main" />
              <span>
                <Translation i18nKey="glossary.accounts" ns="glossary" />
              </span>
            </Typography>

            <div className="mt-4 flex flex-col gap-3">
              {data.selectedInstitutions.map(inst => (
                <div key={inst.id} className="flex flex-col gap-1">
                  <Typography
                    variant="subtitle2"
                    className="!font-medium !text-border-dark"
                    data-testid={`review-institution-${inst.id}`}
                  >
                    {inst.name}
                  </Typography>
                  <div className="flex flex-wrap gap-2">
                    {inst.accounts.map(account => (
                      <Chip
                        key={account.iban}
                        label={account.name}
                        variant="outlined"
                        color="default"
                        size="small"
                        className="!font-semibold"
                        data-testid={`review-account-${account.iban}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-1" variant="outlined" data-testid="advisory-card">
          <CardContent className="!p-4">
            <Typography variant="h5" className="!flex !items-center !gap-1">
              <AppIcon icon="TrendingUp" className="size-5" color="main" />
              <span>
                <Translation i18nKey="glossary.advisory" ns="glossary" />
              </span>
            </Typography>

            <div className="mt-4 flex gap-1">
              <p>{data.advisory_type_label}</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className="col-span-1"
          variant="outlined"
          data-testid="retrocession-card"
        >
          <CardContent className="!p-4">
            <Typography variant="h5" className="!flex !items-center !gap-1">
              <AppIcon icon="DollarSign" className="size-5" color="main" />
              <span>
                <Translation i18nKey="glossary.retrocessions" ns="glossary" />
              </span>
            </Typography>

            <div className="mt-4 flex gap-1">
              <p>{data.retrocession_label}</p>
            </div>
          </CardContent>
        </Card>

        <Card
          className="col-span-1"
          variant="outlined"
          data-testid="commission-card"
        >
          <CardContent className="!p-4">
            <Typography variant="h5" className="!flex !items-center !gap-1">
              <AppIcon icon="Percent" className="size-5" color="main" />
              <span>
                <Translation i18nKey="glossary.commission" ns="glossary" />
              </span>
            </Typography>

            <div className="mt-4 flex gap-1">
              <p>{data.commission_label}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Alert
        severity="success"
        icon={false}
        className="[&_.MuiAlert-message]:!py-0"
        data-testid="confirmation-alert"
      >
        <Translation i18nKey="activation-step5.confirmation" ns="components" />
      </Alert>
    </div>
  );
};
